import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Save, TestTube, Lock, Eye, EyeOff, Check, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStripe = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    secret_key: '',
    publishable_key: '',
    webhook_secret: '',
    currency: 'EUR',
    monthly_price: 9.99,
    yearly_price: 99.99,
    ultimate_monthly_price: 14.99,
    ultimate_yearly_price: 149.99
  });
  const [configured, setConfigured] = useState(false);
  const [source, setSource] = useState('none');
  const [keyPreview, setKeyPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('sendbox_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/stripe`, { headers });
      setConfigured(res.data.configured);
      setSource(res.data.source || 'none');
      setKeyPreview(res.data.secret_key_preview || '');
      setSettings(prev => ({
        ...prev,
        publishable_key: res.data.publishable_key || '',
        currency: res.data.currency || 'EUR',
        monthly_price: res.data.monthly_price || 9.99,
        yearly_price: res.data.yearly_price || 99.99,
        ultimate_monthly_price: res.data.ultimate_monthly_price || 14.99,
        ultimate_yearly_price: res.data.ultimate_yearly_price || 149.99
      }));
    } catch (e) {
      console.error('Error loading Stripe settings:', e);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      if (!payload.secret_key) delete payload.secret_key;
      if (!payload.webhook_secret) delete payload.webhook_secret;
      await axios.post(`${API}/admin/settings/stripe`, payload, { headers });
      toast({ title: 'Configuration Stripe enregistrée' });
      setSettings(prev => ({ ...prev, secret_key: '', webhook_secret: '' }));
      loadSettings();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de sauvegarder', variant: 'destructive' });
    }
    setSaving(false);
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/settings/stripe/test`, {}, { headers });
      toast({ title: 'Stripe fonctionne !', description: res.data.message });
    } catch (e) {
      toast({ title: 'Erreur Stripe', description: e.response?.data?.detail || 'Connexion échouée', variant: 'destructive' });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <Toaster />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/admin/dashboard?tab=settings" className="inline-flex items-center gap-2 text-blue-300/60 hover:text-blue-300 mb-8 transition-colors" data-testid="back-to-settings">
          <ArrowLeft className="w-4 h-4" /> Retour à la configuration
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-violet-500/20 rounded-xl">
            <CreditCard className="w-8 h-8 text-violet-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Configuration Stripe</h1>
            <p className="text-blue-300/60">Configurer le paiement pour les abonnements Pro</p>
          </div>
          {configured && (
            <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Configuré ({source})
            </span>
          )}
        </div>

        <div className="space-y-6">
          {/* API Keys */}
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
            <div className="p-5 border-b border-blue-500/20">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-violet-400" /> Clés API Stripe
              </h2>
              <p className="text-sm text-blue-300/50 mt-1">
                Trouvez vos clés sur{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline inline-flex items-center gap-1">
                  dashboard.stripe.com <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-blue-300 mb-2">Clé secrète (Secret Key) *</label>
                <div className="relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={settings.secret_key}
                    onChange={e => setSettings({ ...settings, secret_key: e.target.value })}
                    placeholder={keyPreview ? `Actuelle : sk_...${keyPreview.replace('...', '')}` : 'sk_test_... ou sk_live_...'}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40 pr-10"
                    data-testid="stripe-secret-key-input"
                  />
                  <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/50 hover:text-blue-300">
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-blue-300/40 mt-1">Laissez vide pour garder la clé actuelle</p>
              </div>

              <div>
                <label className="block text-sm text-blue-300 mb-2">Clé publique (Publishable Key)</label>
                <input
                  type="text"
                  value={settings.publishable_key}
                  onChange={e => setSettings({ ...settings, publishable_key: e.target.value })}
                  placeholder="pk_test_... ou pk_live_..."
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                  data-testid="stripe-publishable-key-input"
                />
              </div>

              <div>
                <label className="block text-sm text-blue-300 mb-2">Webhook Secret (optionnel)</label>
                <input
                  type="password"
                  value={settings.webhook_secret}
                  onChange={e => setSettings({ ...settings, webhook_secret: e.target.value })}
                  placeholder="whsec_..."
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                  data-testid="stripe-webhook-secret-input"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
            <div className="p-5 border-b border-blue-500/20">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-violet-400" /> Tarification Pro
              </h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Devise</label>
                  <select
                    value={settings.currency}
                    onChange={e => setSettings({ ...settings, currency: e.target.value })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                    data-testid="stripe-currency-select"
                  >
                    <option value="EUR">EUR (euro)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Prix mensuel Pro</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.monthly_price}
                    onChange={e => setSettings({ ...settings, monthly_price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                    data-testid="stripe-monthly-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Prix annuel Pro</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.yearly_price}
                    onChange={e => setSettings({ ...settings, yearly_price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                    data-testid="stripe-yearly-price-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Ultimate Pricing */}
          <div className="bg-slate-800/50 border border-purple-500/20 rounded-2xl overflow-hidden">
            <div className="p-5 border-b border-purple-500/20">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-400" /> Tarification Ultimate
              </h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Prix mensuel Ultimate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.ultimate_monthly_price}
                    onChange={e => setSettings({ ...settings, ultimate_monthly_price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                    data-testid="stripe-ultimate-monthly-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Prix annuel Ultimate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.ultimate_yearly_price}
                    onChange={e => setSettings({ ...settings, ultimate_yearly_price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-900/50 border border-purple-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                    data-testid="stripe-ultimate-yearly-price-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-gradient-to-r from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600 text-white px-6 py-5"
              data-testid="save-stripe-btn"
            >
              {saving ? 'Enregistrement...' : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
            </Button>
            <Button
              onClick={testConnection}
              disabled={testing || !configured}
              variant="outline"
              className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10 px-6 py-5"
              data-testid="test-stripe-btn"
            >
              {testing ? 'Test en cours...' : <><TestTube className="w-4 h-4 mr-2" /> Tester la connexion</>}
            </Button>
          </div>

          {/* Help */}
          <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-5 text-sm text-blue-300/50 space-y-2">
            <p><strong className="text-blue-300/70">Comment obtenir vos clés Stripe :</strong></p>
            <ol className="list-decimal list-inside space-y-1 pl-2">
              <li>Connectez-vous sur <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline">dashboard.stripe.com</a></li>
              <li>Allez dans <strong>Developers &gt; API Keys</strong></li>
              <li>Copiez la <strong>Secret key</strong> (sk_test_... ou sk_live_...)</li>
              <li>Copiez la <strong>Publishable key</strong> (pk_test_... ou pk_live_...)</li>
              <li>Pour les webhooks : <strong>Developers &gt; Webhooks &gt; Add endpoint</strong></li>
            </ol>
          </div>
        </div>
      </div>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminStripe;
